import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Link2, TrendingUp, Eye, MousePointer, Phone, Star, ArrowRight, RefreshCw } from 'lucide-react';

const StatCard = ({ label, value, suffix, color }) => (
  <div style={{ background: '#f8fafc', borderRadius: '10px', padding: '14px', textAlign: 'center' }}>
    <p style={{ fontFamily: "'Sora', sans-serif", fontSize: '20px', fontWeight: 800, color: color || '#0f172a' }}>
      {value}{suffix && <span style={{ fontSize: '12px', fontWeight: 600 }}>{suffix}</span>}
    </p>
    <p style={{ fontSize: '11px', color: '#94a3b8', marginTop: '2px' }}>{label}</p>
  </div>
);

export default function IntegrationDataPanel({ provider, title, icon }) {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    api('get', `/api/platform/integrations/${provider}/data`)
      .then(r => {
        setConnected(r.data.connected);
        setData(r.data.data);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [api, provider]);

  if (loading) return (
    <div style={{ background: 'white', borderRadius: '14px', padding: '24px', border: '1px solid #f1f5f9', marginBottom: '16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <RefreshCw size={14} style={{ color: '#D4A017', animation: 'spin 1s linear infinite' }} />
        <span style={{ fontSize: '13px', color: '#94a3b8' }}>Data laden...</span>
      </div>
    </div>
  );

  if (!connected) return (
    <div data-testid={`integration-panel-${provider}-disconnected`} style={{
      background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #f1f5f9',
      marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '14px',
    }}>
      <div style={{ width: '40px', height: '40px', borderRadius: '10px', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
        <Link2 size={18} style={{ color: '#94a3b8' }} />
      </div>
      <div style={{ flex: 1 }}>
        <p style={{ fontSize: '13px', fontWeight: 600, color: '#0f172a' }}>
          {title} koppelen voor live data
        </p>
        <p style={{ fontSize: '11px', color: '#94a3b8' }}>
          Verbind uw account om real-time statistieken te zien
        </p>
      </div>
      <Link to="/platform/integraties" style={{
        display: 'flex', alignItems: 'center', gap: '4px', padding: '8px 14px', borderRadius: '8px',
        background: '#0f172a', color: 'white', fontSize: '12px', fontWeight: 600, textDecoration: 'none',
      }}>
        Koppelen <ArrowRight size={12} />
      </Link>
    </div>
  );

  return (
    <div data-testid={`integration-panel-${provider}`} style={{
      background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #f1f5f9', marginBottom: '16px',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#22c55e' }} />
          <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a' }}>{title} — Live Data</h3>
        </div>
        <span style={{ fontSize: '10px', color: '#94a3b8' }}>Gekoppeld</span>
      </div>

      {provider === 'google_analytics' && data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
          <StatCard label="Bezoekers vandaag" value={data.bezoekers_vandaag} color="#3b82f6" />
          <StatCard label="Deze week" value={data.bezoekers_week} color="#3b82f6" />
          <StatCard label="Deze maand" value={data.bezoekers_maand} color="#3b82f6" />
          <StatCard label="Bounce rate" value={data.bounce_rate} suffix="%" color={data.bounce_rate > 60 ? '#ef4444' : '#22c55e'} />
        </div>
      )}

      {provider === 'google_ads' && data && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
            <StatCard label="Klikken vandaag" value={data.klikken_vandaag} color="#34A853" />
            <StatCard label="CTR" value={data.ctr} suffix="%" color="#3b82f6" />
            <StatCard label="Conversies" value={data.conversies_maand} color="#D4A017" />
            <StatCard label="Kosten/conversie" value={`€${data.kosten_per_conversie}`} color="#0f172a" />
          </div>
          {data.campagnes && (
            <div>
              <p style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>Actieve campagnes</p>
              {data.campagnes.map((c, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px',
                  background: '#f8fafc', borderRadius: '8px', marginBottom: '4px', fontSize: '12px',
                }}>
                  <span style={{ fontWeight: 600, color: '#0f172a' }}>{c.naam}</span>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <span style={{ color: '#64748b' }}>{c.klikken} klikken</span>
                    <span style={{ color: '#64748b' }}>€{c.kosten}</span>
                    <span style={{ color: '#22c55e', fontWeight: 600 }}>{c.status}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {provider === 'google_business' && data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
          <StatCard label="Zoekweergaven" value={data.weergaven_zoeken} color="#4285F4" />
          <StatCard label="Maps weergaven" value={data.weergaven_maps} color="#34A853" />
          <StatCard label="Telefoongesprekken" value={data.telefoongesprekken} color="#D4A017" />
          <StatCard label="Reviews score" value={data.gemiddelde_score} suffix="/5" color="#FBBC05" />
        </div>
      )}

      {provider === 'google_search_console' && data && (
        <div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '14px' }}>
            <StatCard label="Totale klikken" value={data.totale_klikken} color="#EA4335" />
            <StatCard label="Vertoningen" value={data.totale_vertoningen} color="#4285F4" />
            <StatCard label="Gem. positie" value={data.gem_positie} color="#0f172a" />
            <StatCard label="CTR" value={data.gem_ctr} suffix="%" color="#34A853" />
          </div>
          {data.top_zoekwoorden && (
            <div>
              <p style={{ fontSize: '12px', fontWeight: 600, color: '#64748b', marginBottom: '8px' }}>Top zoekwoorden</p>
              {data.top_zoekwoorden.map((z, i) => (
                <div key={i} style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '8px 12px',
                  background: '#f8fafc', borderRadius: '8px', marginBottom: '4px', fontSize: '12px',
                }}>
                  <span style={{ fontWeight: 600, color: '#0f172a' }}>{z.zoekwoord}</span>
                  <div style={{ display: 'flex', gap: '16px' }}>
                    <span style={{ color: '#64748b' }}>{z.klikken} klikken</span>
                    <span style={{ color: '#64748b' }}>Positie {z.positie}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {(provider === 'meta_facebook' || provider === 'meta_instagram') && data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px' }}>
          <StatCard label="Volgers" value={data.volgers} color="#1877F2" />
          <StatCard label="Bereik/week" value={data.bereik_week} color="#3b82f6" />
          <StatCard label="Interacties/week" value={data.interacties_week} color="#E4405F" />
          <StatCard label="Nieuwe volgers" value={data.nieuw_volgers_week} suffix="/week" color="#22c55e" />
        </div>
      )}

      {provider === 'tiktok' && data && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '10px' }}>
          <StatCard label="Volgers" value={data.volgers} color="#000" />
          <StatCard label="Views/week" value={data.video_weergaven_week} color="#EE1D52" />
          <StatCard label="Likes/week" value={data.likes_week} color="#69C9D0" />
        </div>
      )}
    </div>
  );
}
